/**
 * VesselSystems - Vessel systems configuration section
 */

import React from 'react';
import { Checkbox } from '../../../../catalyst/checkbox';

const VesselSystems = ({ vesselConfig, updateConfig }) => {
  const handleOptionalEquipmentToggle = (equipment) => {
    const current = vesselConfig.optionalEquipment || [];
    const updated = current.includes(equipment)
      ? current.filter(e => e !== equipment)
      : [...current, equipment];
    updateConfig({ optionalEquipment: updated });
  };

  return (
    <div className="space-y-6">
      {/* Deck Equipment */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Deck Equipment</h3>
        <div className="space-y-4">
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('crane') || false}
              onChange={() => handleOptionalEquipmentToggle('crane')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Crane/Davit</p>
              <p className="text-sm text-gray-600">Lifting equipment</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('winch_lifting_gear') || false}
              onChange={() => handleOptionalEquipmentToggle('winch_lifting_gear')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Winch/Lifting Gear</p>
              <p className="text-sm text-gray-600">Deck winches and lifting equipment</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('a_frame') || false}
              onChange={() => handleOptionalEquipmentToggle('a_frame')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">A-Frame</p>
              <p className="text-sm text-gray-600">Stern lifting frame</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('moonpool') || false}
              onChange={() => handleOptionalEquipmentToggle('moonpool')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Moonpool</p>
              <p className="text-sm text-gray-600">Through-hull access</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('dive_ladder') || false}
              onChange={() => handleOptionalEquipmentToggle('dive_ladder')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Dive Ladder/Platform</p>
              <p className="text-sm text-gray-600">Diver access system</p>
            </div>
          </label>
        </div>
      </div>

      {/* Accommodation Systems */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Accommodation Systems</h3>
        <div className="space-y-4">
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('hvac') || false}
              onChange={() => handleOptionalEquipmentToggle('hvac')}
              disabled={vesselConfig.autoRequiredAccommodation}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">
                HVAC System
                {vesselConfig.autoRequiredAccommodation && (
                  <span className="ml-2 text-xs text-blue-600">(Required for 24h+ operations)</span>
                )}
              </p>
              <p className="text-sm text-gray-600">Heating, ventilation and air conditioning</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('galley_equipment') || false}
              onChange={() => handleOptionalEquipmentToggle('galley_equipment')}
              disabled={vesselConfig.autoRequiredAccommodation}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">
                Galley Equipment
                {vesselConfig.autoRequiredAccommodation && (
                  <span className="ml-2 text-xs text-blue-600">(Required for 24h+ operations)</span>
                )}
              </p>
              <p className="text-sm text-gray-600">Cooking and food storage facilities</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('watermaker') || false}
              onChange={() => handleOptionalEquipmentToggle('watermaker')}
              disabled={vesselConfig.autoRequiredAccommodation}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">
                Watermaker
                {vesselConfig.autoRequiredAccommodation && (
                  <span className="ml-2 text-xs text-blue-600">(Required for 24h+ operations)</span>
                )}
              </p>
              <p className="text-sm text-gray-600">Fresh water production system</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('sewage_treatment') || false}
              onChange={() => handleOptionalEquipmentToggle('sewage_treatment')}
              disabled={vesselConfig.autoRequiredSewage}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">
                Sewage Treatment
                {vesselConfig.autoRequiredSewage && (
                  <span className="ml-2 text-xs text-blue-600">(Auto-required)</span>
                )}
              </p>
              <p className="text-sm text-gray-600">Waste water treatment system</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('laundry') || false}
              onChange={() => handleOptionalEquipmentToggle('laundry')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Laundry Facilities</p>
              <p className="text-sm text-gray-600">Washing machine and dryer</p>
            </div>
          </label>
        </div>
      </div>

      {/* Power & Propulsion */}
      <div>
        <h3 className="text-base font-medium text-gray-900 mb-4">Power & Propulsion</h3>
        <div className="space-y-4">
          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('generator') || false}
              onChange={() => handleOptionalEquipmentToggle('generator')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Generator</p>
              <p className="text-sm text-gray-600">Auxiliary power generation</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('shore_power') || false}
              onChange={() => handleOptionalEquipmentToggle('shore_power')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Shore Power Connection</p>
              <p className="text-sm text-gray-600">External power connection</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('bow_thruster') || false}
              onChange={() => handleOptionalEquipmentToggle('bow_thruster')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Bow Thruster</p>
              <p className="text-sm text-gray-600">Maneuvering aid</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('stern_thruster') || false}
              onChange={() => handleOptionalEquipmentToggle('stern_thruster')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Stern Thruster</p>
              <p className="text-sm text-gray-600">Aft maneuvering aid</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('dynamic_positioning') || false}
              onChange={() => handleOptionalEquipmentToggle('dynamic_positioning')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Dynamic Positioning (DP)</p>
              <p className="text-sm text-gray-600">Computer-controlled position keeping</p>
            </div>
          </label>

          <label className="flex items-start gap-3">
            <Checkbox
              checked={vesselConfig.optionalEquipment?.includes('stabilizers') || false}
              onChange={() => handleOptionalEquipmentToggle('stabilizers')}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900">Stabilizers</p>
              <p className="text-sm text-gray-600">Roll reduction system</p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default VesselSystems;